import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { stageAPI } from '../services/api';
import { dateFr } from '../services/dates';
import ChampDate from './ChampDate';
import StageModale from './StageModale';
import StageCertifDepotPublic from './StageCertifDepotPublic';
import StageCertifMaitrePublic from './StageCertifMaitrePublic';
import { MenuFormationsCertif, etudiantsDe, quiCertifie } from './StageFormationsProcessus';

/**
 * SUIVI DE STAGE — Certification › Paramétrage › Processus (lot C.0)
 * ===================================================================
 * Spec     : doc/modules/suivi_de_stage_certification.md § 2 a) Processus
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#cpc-processus, #certif-proc-editor)
 *
 * ── CE QUE LA MAQUETTE DESSINE, ET CE QUI S'Y AJOUTE ───────────────────────
 * La liste (Formation · Processus · Période d'activité · Certification avant le ·
 * Livrables · Dossiers · État · Actions), puis l'éditeur en CARTE SOUS LA LISTE —
 * pas en ligne du tableau comme les processus des Stagiaires : c'est la maquette
 * qui tranche (règle 4). Trois étapes : ① Identité, ② Période et dates,
 * ③ Livrables et, pour chacun, ce que le maître atteste.
 * S'ajoutent, décidés au cadrage du 2026-10-05 :
 *   · CC3 — « Supprimer » un processus JAMAIS lancé (la maquette n'a pas de corbeille) ;
 *   · CC4 — « Voir la page de l'étudiant » (lot C.2) et « Voir la page du maître » (lot C.3, aussi sur
 *     l'œil de la liste) : les vraies pages, sur des données fictives (C28).
 *
 * ── LE VERROU (C18) ─────────────────────────────────────────────────────────
 * Un processus lancé (figé au premier envoi, lot C.4) garde ses livrables et ses
 * attestations : l'écran les grise et ne les ENVOIE plus ; le serveur refuse de
 * toute façon (409) une liste différente. Le nom et les dates restent modifiables.
 *
 * ── IL N'Y A AUCUN BOUTON D'ENVOI ICI (C30) ────────────────────────────────
 * L'envoi se fait depuis le tableau de bord. Une ligne le rappelle dans l'éditeur.
 */


/**
 * C28 / E116 — l'aperçu de la page de l'étudiant : la VRAIE page, sur des données
 * fictives, construites depuis le brouillon de l'éditeur (livrables en cours de
 * saisie, avertissement du département). Rien n'est enregistré, rien n'est envoyé.
 */
function etudiantFictif(brouillon, formations, reglages) {
    const formation = formations.find((f) => f.id === (brouillon.formations || [])[0]);
    return {
        dossier: { nom: 'DUBOIS', prenom: 'Emma', formation_nom: formation?.nom || 'Formation',
                   departement_nom: 'Département', processus_nom: brouillon.nom || '(processus sans nom)',
                   date_limite_depot: brouillon.date_limite_depot || null },
        certificateur: { nom: 'M. Lefebvre', entreprise: 'Entreprise Exemple' },
        certificateur_role: "maître de stage ou d'apprentissage",
        ouvert: true, message: null,
        avertissement: reglages.avertissement || '',
        livrables: brouillon.livrables.filter((x) => x.actif !== false).map((x, k) => ({
            livrable_id: `a${k}`, libelle: x.libelle || '(livrable sans libellé)', obligatoire: x.obligatoire !== false,
            formats: x.formats_acceptes === 'pdf' ? ['pdf'] : ['pdf', 'jpeg', 'png'],
            hors_entreprise_possible: !!x.hors_entreprise_possible, courante: null, declaration: null })),
        taille_max_octets: 10 * 1024 * 1024
    };
}

/**
 * C28 / E116 — l'aperçu de la page du MAÎTRE (lot C.3) : la vraie page, sur des données
 * fictives, depuis le brouillon de l'éditeur OU un processus de la liste. Chaque livrable
 * actif y est « à décider », avec SES attestations et les libellés du département.
 */
function maitreFictif({ nom, livrables, periodeDebut, periodeFin, formationNom }, reglages) {
    const periode = periodeDebut && periodeFin ? `${dateFr(periodeDebut)} → ${dateFr(periodeFin)}` : '';
    return {
        entete: { formation_nom: formationNom || 'Formation', departement_nom: 'Département', processus_nom: nom || '(processus sans nom)', demandeur: null },
        texte_intro: reglages.texte_intro || '',
        etudiant: { nom: 'DUBOIS', prenom: 'Emma', entreprise: 'Entreprise Exemple', processus: nom || '(processus sans nom)', periode },
        signataire: { nom: 'M. Lefebvre', fonction: 'Responsable qualité' },
        ouvert: true,
        documents: livrables.filter((x) => x.actif !== false).map((x, k) => ({
            type: 'a_decider', livrable_id: `a${k}`, depot_id: `d${k}`, libelle: x.libelle || '(livrable sans libellé)',
            nom_fichier: `document_${k + 1}.pdf`, taille_octets: 2 * 1024 * 1024, ouvert_le: null,
            attestations: [
                x.attest_lecture && { cle: 'lecture', libelle: reglages.libelle_lecture },
                x.attest_periode && { cle: 'periode', libelle: periode ? `${reglages.libelle_periode} (${periode})` : reglages.libelle_periode },
                x.attest_confidentialite && { cle: 'confidentialite', libelle: reglages.libelle_confidentialite }
            ].filter(Boolean)
        }))
    };
}

let compteur = 0;
const cle = () => `l${++compteur}`;

/** Un livrable neuf : Lu + Période, comme les deux premières lignes de la maquette. */
const livrableNeuf = () => ({
    _cle: cle(), id: null, libelle: '', obligatoire: true, formats_acceptes: 'pdf',
    attest_lecture: true, attest_periode: true, attest_confidentialite: false,
    hors_entreprise_possible: false, actif: true, utilise: false
});

/** Les colonnes d'attestation, dans l'ordre de la maquette ; leur info-bulle est le libellé du département. */
const COLONNES_ATTEST = [
    { champ: 'attest_lecture',         entete: 'Lu',      reglage: 'libelle_lecture' },
    { champ: 'attest_periode',         entete: 'Période', reglage: 'libelle_periode' },
    { champ: 'attest_confidentialite', entete: 'Confid.', reglage: 'libelle_confidentialite' }
];

function Bascule({ actif, onBasculer, verrouille, titre, etiquette }) {
    return (
        <button
            type="button"
            className={'toggle' + (actif ? ' on' : '') + (verrouille ? ' cfg-locked' : '')}
            aria-pressed={actif}
            aria-label={etiquette}
            title={titre}
            disabled={verrouille}
            onClick={onBasculer}
        />
    );
}

/**
 * Lot L1a (E169) — `seulement` : le processus de la VIGNETTE ouverte. La liste ne montre que lui, son éditeur est
 * ouvert d'office et se rouvre après un enregistrement ; « Reprendre l'année précédente » et « Nouveau processus »
 * sont sur l'accueil. `onEcrit` : après une écriture ; `onSupprime` : le processus n'existe plus (retour à l'accueil).
 */
function StageCertifProcessus({ donnees, departementId, anneeId, modifiable, onMiseAJour, seulement = null, onEcrit = null, onSupprime = null, manquants = [] }) {
    const [ouvert, setOuvert]         = useState(null);   // id · 'new' · null
    const [brouillon, setBrouillon]   = useState(null);
    const [enCours, setEnCours]       = useState(false);
    const [erreur, setErreur]         = useState(null);
    const [aSupprimer, setASupprimer] = useState(null);
    const [confirmeN1, setConfirmeN1] = useState(false);
    const [bilan, setBilan]           = useState(null);
    // C28 : l'aperçu de la page de l'étudiant (le brouillon reste intact derrière).
    const [apercuEtudiant, setApercuEtudiant] = useState(null);
    // Lot C.3 : l'aperçu de la page du maître ({ n, page, titre }).
    const [apercuMaitre, setApercuMaitre] = useState(null);
    const glisse = useRef(null);
    const carteEditeur = useRef(null);

    const processus  = donnees.processus || [];
    const formations = donnees.formations || [];
    const reglages   = donnees.reglages || {};
    const courant    = useMemo(() => processus.find((p) => p.id === ouvert) || null, [processus, ouvert]);
    const fige       = !!courant?.lance;
    // « Reste à faire » : un champ signalé manquant s'encadre de rouge, et redevient normal dès qu'il est rempli.
    const rempli = {
        periode: Boolean(brouillon?.periode_debut && brouillon?.periode_fin),
        date_limite_certification: Boolean(brouillon?.date_limite_certification),
        livrables: (brouillon?.livrables || []).some((x) => x.actif !== false && String(x.libelle || '').trim())
    };
    const rouge = (cle) => manquants.includes(cle) && !rempli[cle];
    const classeReste = (cle, base) => base + (rouge(cle) ? ' reste-manquant' : '');
    const livrablesBloques = !modifiable || fige;

    // L'éditeur s'ouvre SOUS la liste : on l'amène à l'écran, sinon un clic sur
    // « Modifier » en haut d'une longue liste semblerait ne rien faire.
    useEffect(() => {
        if (ouvert && carteEditeur.current?.scrollIntoView) {
            carteEditeur.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [ouvert]);

    // ── Ouverture / fermeture ───────────────────────────────────────────────

    const ouvrir = useCallback((cible, depuis = null) => {
        setErreur(null);
        if (cible === 'new') {
            setOuvert('new');
            setBrouillon(depuis
                // « Dupliquer » : livrables et attestations repris, DATES VIDÉES (spec § 2 a).
                ? {
                    formations: (depuis.formations || []).map((f) => f.id), nom: `${depuis.nom} (copie)`.slice(0, 200),
                    periode_debut: '', periode_fin: '', date_limite_depot: '', date_limite_certification: '',
                    delai_relance_sem: depuis.delai_relance_sem || 1,
                    livrables: (depuis.livrables || []).filter((x) => x.actif)
                        .map((x) => ({ ...x, id: null, utilise: false, _cle: cle() }))
                }
                : {
                    formations: [], nom: '',
                    periode_debut: '', periode_fin: '', date_limite_depot: '', date_limite_certification: '',
                    delai_relance_sem: 1, livrables: [livrableNeuf()]
                });
            return;
        }
        if (ouvert === cible) { setOuvert(null); setBrouillon(null); return; }
        const p = processus.find((x) => x.id === cible);
        if (!p) return;
        setOuvert(cible);
        setBrouillon({
            formations: (p.formations || []).map((f) => f.id), nom: p.nom,
            periode_debut: p.periode_debut || '', periode_fin: p.periode_fin || '',
            date_limite_depot: p.date_limite_depot || '', date_limite_certification: p.date_limite_certification || '',
            delai_relance_sem: p.delai_relance_sem || 1,
            livrables: (p.livrables || []).map((x) => ({ ...x, _cle: cle() }))
        });
    }, [ouvert, processus]);

    const fermer = useCallback(() => { setOuvert(null); setBrouillon(null); setErreur(null); }, []);

    // E169 : dans une vignette, l'éditeur de SON processus est toujours ouvert (au montage, après un enregistrement).
    useEffect(() => {
        if (seulement && ouvert === null && processus.some((p) => p.id === seulement)) ouvrir(seulement);
    }, [seulement, ouvert, processus, ouvrir]);
    const affiches = seulement ? processus.filter((p) => p.id === seulement) : processus;

    const poser = useCallback((champ, valeur) => setBrouillon((b) => (b ? { ...b, [champ]: valeur } : b)), []);

    const poserLivrable = useCallback((k, champ, valeur) => setBrouillon((b) => (b ? {
        ...b, livrables: b.livrables.map((x) => (x._cle === k ? { ...x, [champ]: valeur } : x))
    } : b)), []);

    const retirerLivrable = useCallback((k) => setBrouillon((b) => (b ? {
        ...b, livrables: b.livrables.filter((x) => x._cle !== k)
    } : b)), []);

    const ajouterLivrable = useCallback(() => setBrouillon((b) => (b ? {
        ...b, livrables: [...b.livrables, livrableNeuf()]
    } : b)), []);

    const deplacer = useCallback((depart, cible) => setBrouillon((b) => {
        if (!b || depart === cible || cible < 0 || cible >= b.livrables.length) return b;
        const copie = [...b.livrables];
        const [x] = copie.splice(depart, 1);
        copie.splice(cible, 0, x);
        return { ...b, livrables: copie };
    }), []);

    // ── Écritures ───────────────────────────────────────────────────────────

    const enregistrer = useCallback(async () => {
        if (!brouillon) return;
        setEnCours(true);
        setErreur(null);
        try {
            const charge = {
                // E171 (fin, v86) : les formations (la première devient la principale).
                formations: brouillon.formations || [],
                nom: brouillon.nom,
                periode_debut: brouillon.periode_debut || null,
                periode_fin: brouillon.periode_fin || null,
                date_limite_depot: brouillon.date_limite_depot || null,
                date_limite_certification: brouillon.date_limite_certification || null,
                delai_relance_sem: brouillon.delai_relance_sem
            };
            // ⚠️ Processus figé : les livrables ne partent PAS (le serveur les refuserait).
            if (!fige) {
                charge.livrables = brouillon.livrables.map((x) => ({
                    id: x.id, libelle: x.libelle, obligatoire: x.obligatoire !== false,
                    formats_acceptes: x.formats_acceptes,
                    attest_lecture: !!x.attest_lecture, attest_periode: !!x.attest_periode,
                    attest_confidentialite: !!x.attest_confidentialite,
                    hors_entreprise_possible: !!x.hors_entreprise_possible, actif: x.actif !== false
                }));
            }
            const r = ouvert === 'new'
                ? await stageAPI.creerProcessusCertif(departementId, anneeId, charge)
                : await stageAPI.modifierProcessusCertif(ouvert, departementId, charge);
            onMiseAJour({ processus: r.data.processus });
            const nom = brouillon.nom.trim();
            setOuvert(null);
            setBrouillon(null);
            if (onEcrit) onEcrit();
            setBilan({
                titre: ouvert === 'new' ? 'Processus créé' : 'Processus enregistré',
                lignes: [
                    `« ${nom} » est enregistré. Il n'envoie rien : l'envoi se fera depuis le tableau de bord.`,
                    !charge.periode_debut || !charge.periode_fin || !charge.date_limite_depot || !charge.date_limite_certification
                        ? "Des dates restent à saisir : elles seront exigées avant le premier envoi."
                        : null
                ].filter(Boolean)
            });
        } catch (e) {
            const d = e.response?.data;
            setErreur(d?.motifs?.length > 1 && e.response?.status === 400
                ? d.motifs.join(' · ')
                : [d?.error, d?.detail].filter(Boolean).join(' — ') || e.message || "L'enregistrement a échoué");
        } finally {
            setEnCours(false);
        }
    }, [brouillon, ouvert, fige, departementId, anneeId, onMiseAJour, onEcrit]);

    const supprimer = useCallback(async () => {
        if (!aSupprimer) return;
        setEnCours(true);
        try {
            const r = await stageAPI.supprimerProcessusCertif(aSupprimer.id, departementId);
            onMiseAJour({ processus: r.data.processus });
            if (ouvert === aSupprimer.id) { setOuvert(null); setBrouillon(null); }
            setBilan({ titre: 'Processus supprimé', lignes: [`« ${aSupprimer.nom} » (${aSupprimer.formation_nom}) a été supprimé avec ses livrables.`],
                       apres: aSupprimer.id === seulement ? onSupprime : null });
            setASupprimer(null);
        } catch (e) {
            setASupprimer(null);
            setErreur([e.response?.data?.error, e.response?.data?.detail].filter(Boolean).join(' — ') || 'La suppression a échoué');
        } finally {
            setEnCours(false);
        }
    }, [aSupprimer, departementId, ouvert, onMiseAJour, seulement, onSupprime]);

    const reprendreN1 = useCallback(async () => {
        setEnCours(true);
        setErreur(null);
        try {
            const r = await stageAPI.reprendreProcessusCertifN1(departementId, anneeId);
            onMiseAJour({ processus: r.data.processus });
            setConfirmeN1(false);
            setBilan({
                titre: `Processus repris de ${r.data.annee_source.libelle}`,
                lignes: [
                    `${r.data.copies.length} processus copié(s) — sans leurs dates, qu'il reste à saisir.`,
                    ...r.data.copies.map((c) => `· ${c.libelle} — ${c.livrables} livrable(s)`),
                    ...(r.data.ignores.length
                        ? [`${r.data.ignores.length} ignoré(s) :`, ...r.data.ignores.map((c) => `· ${c.libelle} — ${c.motif}`)]
                        : [])
                ]
            });
        } catch (e) {
            setConfirmeN1(false);
            setErreur([e.response?.data?.error, e.response?.data?.detail].filter(Boolean).join(' — ') || 'La reprise a échoué');
        } finally {
            setEnCours(false);
        }
    }, [departementId, anneeId, onMiseAJour]);

    // ── L'ÉDITEUR ───────────────────────────────────────────────────────────

    const titreEditeur = ouvert === 'new'
        ? 'Nouveau processus'
        : `Processus — ${courant?.titre || `${courant?.formation_nom || ''} · ${courant?.nom || ''}`}`;

    const editeur = brouillon && (
        <div className="card certif-editeur" ref={carteEditeur}>
            <div className="card-header">
                <span><i className="fa-solid fa-pen" style={{ color: 'var(--stg-vert-d)' }} /> {titreEditeur}</span>
                {/* E169 : dans une vignette, l'éditeur de son processus ne se ferme pas. */}
                {(!seulement || ouvert !== seulement) && (
                <button type="button" className="btn btn-outline btn-sm" onClick={fermer} disabled={enCours}>
                    <i className="fa-solid fa-xmark" /> Fermer
                </button>
                )}
            </div>
            <div style={{ padding: '14px 16px' }}>
                {fige && (
                    <div className="certif-fige">
                        <i className="fa-solid fa-lock" style={{ marginTop: 1 }} />
                        <span>
                            <b>Processus figé le {new Date(courant.fige_le).toLocaleDateString('fr-FR')}</b> (premier envoi du lien) :
                            {' '}les dossiers lancés gardent ces livrables et ces attestations. Une modification vaudra pour les
                            prochains processus. Le nom et les dates restent modifiables.
                        </span>
                    </div>
                )}

                {/* ① IDENTITÉ */}
                <div className="cfg-step">
                    <div className="cfg-step-header">
                        <span className="cfg-step-num" aria-hidden="true">①</span>
                        <div style={{ flex: 1 }}>
                            <div className="cfg-step-title">
                                <i className="fa-solid fa-id-card" style={{ color: 'var(--stg-vert-d)', fontSize: 10, marginRight: 4 }} />Identité
                            </div>
                        </div>
                    </div>
                    <div className="cfg-step-body">
                        {/* E171 (fin, v86 — maquette rév. 3) : FORMATIONS CONCERNÉES, plusieurs possibles ; une formation peut
                            être dans plusieurs processus de certification. Lancé : on peut en ajouter (ses inscrits arrivent
                            comme des retardataires), pas retirer une formation qui a déjà des dossiers (le serveur refuse). */}
                        <div className="cfg-param-row" style={{ alignItems: 'flex-start' }}>
                            <label>Formations concernées <span className="form-required">*</span></label>
                            <div style={{ flex: 1 }}>
                                <MenuFormationsCertif formations={formations} coches={brouillon.formations || []} disabled={!modifiable}
                                    onBasculer={(id) => poser('formations', formations.map((f) => f.id).filter((x) =>
                                        (x === id ? !(brouillon.formations || []).includes(id) : (brouillon.formations || []).includes(x))))} />
                                <span style={{ fontSize: 11, color: 'var(--stg-muted)', marginLeft: 8 }}>plusieurs possibles · plusieurs processus par formation possibles</span>
                            </div>
                        </div>
                        {(() => {
                            const avant = courant?.formations || [];
                            const ids = [...new Set([...(brouillon.formations || []), ...avant.map((x) => x.id)])];
                            if (!ids.length) return <div className="stg-encart stg-encart-alerte" style={{ margin: '0 0 8px' }}><i className="fa-solid fa-triangle-exclamation" /><span>Aucune formation cochée.</span></div>;
                            return (
                                <table className="cp-formations">
                                    <thead><tr><th>Formation</th><th>Étudiants</th><th>Qui certifie</th></tr></thead>
                                    <tbody>
                                        {ids.map((id) => {
                                            const f = formations.find((x) => x.id === id) || avant.find((x) => x.id === id) || { nom: `#${id}` };
                                            const retiree = !(brouillon.formations || []).includes(id);
                                            const dossiers = Number(avant.find((x) => x.id === id)?.nb_dossiers || 0);
                                            return (
                                                <tr key={id} className={retiree ? 'retiree' : ''}>
                                                    <td><b>{f.nom}</b></td>
                                                    <td>{etudiantsDe(f)}{dossiers ? <span style={{ color: 'var(--stg-muted)' }}> · {dossiers} dossier{dossiers > 1 ? 's' : ''}</span> : null}</td>
                                                    <td>{!retiree ? quiCertifie(f)
                                                        : fige && dossiers ? <><i className="fa-solid fa-triangle-exclamation" /> Processus lancé : {dossiers} dossier(s), elle ne peut pas être retirée</>
                                                        : <><i className="fa-solid fa-circle-info" /> Décochée : retirée à l'enregistrement</>}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        })()}
                        <div className="cfg-param-row">
                            <label htmlFor="cp-nom">Nom du processus <span className="form-required">*</span></label>
                            <input
                                id="cp-nom" className="input-sm" style={{ flex: 1 }} maxLength={200}
                                placeholder="Ex : SAÉ du S3, Rapport et soutenance"
                                value={brouillon.nom} disabled={!modifiable}
                                onChange={(e) => poser('nom', e.target.value)}
                            />
                        </div>
                        {/* E171 (fin) : « Qui certifie » est dit PAR FORMATION dans le tableau ci-dessus (la ligne générale
                            qui était ici le répétait). */}
                    </div>
                </div>

                {/* ② PÉRIODE ET DATES */}
                <div className="cfg-step">
                    <div className="cfg-step-header">
                        <span className="cfg-step-num" aria-hidden="true">②</span>
                        <div style={{ flex: 1 }}>
                            <div className="cfg-step-title">
                                <i className="fa-solid fa-calendar-days" style={{ color: 'var(--stg-vert-d)', fontSize: 10, marginRight: 4 }} />Période d'activité et dates limites
                            </div>
                        </div>
                    </div>
                    <div className="cfg-step-body">
                        <div data-reste="periode" className={classeReste('periode', 'cfg-param-row')}>
                            <label htmlFor="cp-pdeb">Période d'activité{rouge('periode') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <ChampDate id="cp-pdeb" className="input-sm" disabled={!modifiable}
                                       value={brouillon.periode_debut} onChange={(e) => poser('periode_debut', e.target.value)} />
                                <span style={{ fontSize: 12, color: 'var(--stg-muted)' }}>au</span>
                                <ChampDate id="cp-pfin" className="input-sm" disabled={!modifiable} aria-label="Fin de la période d'activité"
                                       value={brouillon.periode_fin} onChange={(e) => poser('periode_fin', e.target.value)} />
                            </div>
                        </div>
                        <div className="cfg-param-row">
                            <label />
                            <span style={{ fontSize: 10, color: 'var(--stg-muted)' }}>
                                <i className="fa-solid fa-lightbulb" /> Elle correspond au semestre, mais se saisit ici : aucune liaison.
                                {' '}Le maître la voit à côté de l'attestation « réalisé durant la période d'activité ».
                            </span>
                        </div>
                        <div className="cfg-param-row">
                            <label htmlFor="cp-ldep">Date limite de dépôt</label>
                            <ChampDate id="cp-ldep" className="input-sm" style={{ width: 'auto', flex: 'none' }} disabled={!modifiable}
                                   value={brouillon.date_limite_depot} onChange={(e) => poser('date_limite_depot', e.target.value)} />
                        </div>
                        <div data-reste="date_limite_certification" className={classeReste('date_limite_certification', 'cfg-param-row')}>
                            <label htmlFor="cp-lcert">Date limite de certification{rouge('date_limite_certification') && <span className="reste-note"> — à remplir pour terminer le paramétrage</span>}</label>
                            <ChampDate id="cp-lcert" className="input-sm" style={{ width: 'auto', flex: 'none' }} disabled={!modifiable}
                                   value={brouillon.date_limite_certification} onChange={(e) => poser('date_limite_certification', e.target.value)} />
                        </div>
                        <div className="cfg-param-row">
                            <label htmlFor="cp-delai">Délai de relance</label>
                            <div className="cfg-delai">
                                <input id="cp-delai" type="number" min="1" max="52" className="input-sm" style={{ width: 55 }}
                                       disabled={!modifiable} value={brouillon.delai_relance_sem}
                                       onChange={(e) => poser('delai_relance_sem', e.target.value)} />
                                <span>sem. sans réponse</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ③ LIVRABLES */}
                <div data-reste="livrables" className={classeReste('livrables', 'cfg-step')}>
                    <div className="cfg-step-header">
                        <span className="cfg-step-num" aria-hidden="true">③</span>
                        <div style={{ flex: 1 }}>
                            <div className="cfg-step-title">
                                <i className="fa-solid fa-file-lines" style={{ color: 'var(--stg-vert-d)', fontSize: 10, marginRight: 4 }} />Livrables à certifier — et, pour chacun, ce que le maître atteste
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--stg-muted)', marginTop: 2 }}>
                                Libellés libres · une soutenance se certifie par son support déposé · les libellés des attestations se règlent dans « Attestations »
                            </div>
                        </div>
                    </div>
                    <div className="cfg-step-body">
                        <table className="certif-livrables-table">
                            <thead>
                                <tr>
                                    <th />
                                    <th style={{ textAlign: 'left' }}>Livrable</th>
                                    <th>Obligatoire</th>
                                    <th>Formats</th>
                                    {COLONNES_ATTEST.map((c) => <th key={c.champ} title={reglages[c.reglage]}>{c.entete}</th>)}
                                    <th title="L'étudiant peut le déclarer « sujet de la formation (hors entreprise) » : pas de dépôt">Hors entr. possible</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {brouillon.livrables.map((x, i) => {
                                    const n = i + 1;
                                    const bloque = livrablesBloques || x.actif === false;
                                    return (
                                        <tr key={x._cle} className={x.actif === false ? 'inactif' : undefined}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => { e.preventDefault(); if (glisse.current !== null) deplacer(glisse.current, i); glisse.current = null; }}>
                                            <td>
                                                <button type="button" className="critere-drag" draggable={!livrablesBloques} disabled={livrablesBloques}
                                                        title="Glisser pour réordonner (ou flèches haut / bas)" aria-label={`Déplacer le livrable ${n}`}
                                                        onDragStart={(e) => { glisse.current = i; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'ArrowUp') { e.preventDefault(); deplacer(i, i - 1); }
                                                            if (e.key === 'ArrowDown') { e.preventDefault(); deplacer(i, i + 1); }
                                                        }}>
                                                    <i className="fa-solid fa-grip-vertical" />
                                                </button>
                                            </td>
                                            <td>
                                                <input type="text" className="input-sm" maxLength={300} placeholder="Ex : Rapport de la SAÉ 3.01"
                                                       aria-label={`Libellé du livrable ${n}`} value={x.libelle} disabled={bloque}
                                                       onChange={(e) => poserLivrable(x._cle, 'libelle', e.target.value)} />
                                            </td>
                                            <td>
                                                <Bascule actif={x.obligatoire !== false} verrouille={bloque}
                                                         etiquette={`Obligatoire — livrable ${n}`} titre="Obligatoire / Facultatif"
                                                         onBasculer={() => poserLivrable(x._cle, 'obligatoire', x.obligatoire === false)} />
                                            </td>
                                            <td>
                                                <select className="input-sm" value={x.formats_acceptes} disabled={bloque}
                                                        aria-label={`Formats — livrable ${n}`}
                                                        onChange={(e) => poserLivrable(x._cle, 'formats_acceptes', e.target.value)}>
                                                    <option value="pdf">PDF</option>
                                                    <option value="pdf,image">PDF + image</option>
                                                </select>
                                            </td>
                                            {COLONNES_ATTEST.map((c) => (
                                                <td key={c.champ}>
                                                    <Bascule actif={!!x[c.champ]} verrouille={bloque}
                                                             etiquette={`${c.entete} — livrable ${n}`} titre={reglages[c.reglage]}
                                                             onBasculer={() => poserLivrable(x._cle, c.champ, !x[c.champ])} />
                                                </td>
                                            ))}
                                            <td>
                                                <Bascule actif={!!x.hors_entreprise_possible} verrouille={bloque}
                                                         etiquette={`Hors entreprise possible — livrable ${n}`}
                                                         titre="L'étudiant peut déclarer avoir traité le sujet proposé par la formation"
                                                         onBasculer={() => poserLivrable(x._cle, 'hors_entreprise_possible', !x.hors_entreprise_possible)} />
                                            </td>
                                            <td>
                                                {fige ? (
                                                    <i className="fa-solid fa-lock" style={{ color: 'var(--stg-muted)', fontSize: 11 }} title="Processus figé" />
                                                ) : x.utilise ? (
                                                    // E113 : déjà déposé → il se désactive, il ne se supprime pas.
                                                    <button type="button" className="action-btn" disabled={!modifiable}
                                                            title={x.actif === false ? 'Réactiver ce livrable' : 'Déjà déposé : il se désactive, il ne se supprime pas'}
                                                            onClick={() => poserLivrable(x._cle, 'actif', x.actif === false)}>
                                                        <i className={'fa-solid ' + (x.actif === false ? 'fa-eye' : 'fa-eye-slash')} />
                                                    </button>
                                                ) : (
                                                    <button type="button" className="action-btn" title="Retirer ce livrable" disabled={!modifiable}
                                                            style={{ color: '#dc2626', borderColor: '#fecaca' }}
                                                            onClick={() => retirerLivrable(x._cle)}>
                                                        <i className="fa-solid fa-trash" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div style={{ fontSize: 10, color: 'var(--stg-muted)', margin: '6px 0' }}>
                            <i className="fa-solid fa-circle-info" /> Au moins une attestation par livrable. « Hors entreprise possible » : l'étudiant peut
                            {' '}déclarer avoir traité le sujet proposé par la formation — pas de dépôt, pas de certification, sous un avertissement disciplinaire.
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <button type="button" className="btn btn-outline btn-sm" disabled={livrablesBloques}
                                    title={fige ? 'Processus figé' : undefined} onClick={ajouterLivrable}>
                                <i className="fa-solid fa-plus" /> Ajouter un livrable
                            </button>
                            <button type="button" className="btn btn-outline btn-sm" disabled
                                    title="Le programme de formation n'est pas encore rempli">
                                <i className="fa-solid fa-list-check" /> Choisir dans le programme
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 10 }}>
                    <i className="fa-solid fa-paper-plane" /> Le paramétrage décrit le processus ; <b>l'envoi se fait depuis le tableau de bord</b>
                    {' '}(choix du processus, « Envoyer le lien à la promo »).
                </div>

                {erreur && (
                    <div className="stg-encart stg-encart-erreur" style={{ marginTop: 12, marginBottom: 0 }}>
                        <i className="fa-solid fa-circle-exclamation" />
                        <span>{erreur}</span>
                    </div>
                )}

                <div className="certif-pied">
                    <button type="button" className="btn btn-green btn-sm" disabled={enCours || !modifiable} onClick={enregistrer}>
                        {enCours
                            ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Enregistrement…</>
                            : <><i className="fa-solid fa-check" /> Enregistrer</>}
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={fermer} disabled={enCours}>Annuler</button>
                    {/* E177 (maquette rév. 14) : jamais lancé → supprimer ici aussi ; lancé → on le dit (le serveur refuserait). */}
                    {ouvert !== 'new' && courant && (fige
                        ? <span className="cfg-non-supprimable"><i className="fa-solid fa-lock" /> Lancé le {new Date(courant.fige_le).toLocaleDateString('fr-FR')} : ce processus ne se supprime plus</span>
                        : <button type="button" className="btn btn-outline btn-sm cfg-supprimer" disabled={enCours || !modifiable}
                                  onClick={() => setASupprimer(courant)}>
                              <i className="fa-solid fa-trash" /> Supprimer ce processus
                          </button>)}
                    {/* CC4 : les vraies pages arrivent aux lots C.2 (étudiant) et C.3 (maître). */}
                    <div className="certif-apercus">
                        <button type="button" className="lien-apercu" title="La vraie page, sur des données fictives"
                                onClick={() => setApercuEtudiant({ n: Date.now(), etat: etudiantFictif(brouillon, formations, reglages) })}>
                            <i className="fa-solid fa-eye" /> Voir la page de l'étudiant
                        </button>
                        <button type="button" className="lien-apercu" title="La vraie page, sur des données fictives"
                                onClick={() => setApercuMaitre({ n: Date.now(), retour: "Retour à l'éditeur", titre: brouillon.nom || 'Nouveau processus',
                                    page: maitreFictif({ nom: brouillon.nom, livrables: brouillon.livrables, periodeDebut: brouillon.periode_debut,
                                        periodeFin: brouillon.periode_fin,
                                        formationNom: formations.find((f) => f.id === (brouillon.formations || [])[0])?.nom }, reglages) })}>
                            <i className="fa-solid fa-eye" /> Voir la page du maître
                        </button>
                    </div>
                </div>
                {!modifiable && (
                    <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 6 }}>
                        <i className="fa-solid fa-circle-info" /> Lecture seule : votre profil ne permet pas de modifier ce module.
                    </div>
                )}
            </div>
        </div>
    );

    // ── L'APERÇU DE LA PAGE DU MAÎTRE (C28, lot C.3) ───────────────────────
    if (apercuMaitre) {
        return (
            <div>
                <div className="apercu-barre">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setApercuMaitre(null)}>
                        <i className="fa-solid fa-arrow-left" /> {apercuMaitre.retour}
                    </button>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                        Aperçu — Certification des documents{' '}
                        <span style={{ color: 'var(--stg-muted)', fontWeight: 400 }}>(vue maître de stage ou d'apprentissage)</span>
                    </div>
                    <span className="apercu-modele">{apercuMaitre.titre}</span>
                </div>
                <div className="apercu-cadre"><StageCertifMaitrePublic key={apercuMaitre.n} page={apercuMaitre.page} apercu /></div>
            </div>
        );
    }

    // ── L'APERÇU DE LA PAGE DE L'ÉTUDIANT (C28) ────────────────────────────
    if (apercuEtudiant) {
        return (
            <div>
                <div className="apercu-barre">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setApercuEtudiant(null)}>
                        <i className="fa-solid fa-arrow-left" /> Retour à l'éditeur
                    </button>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                        Aperçu — Dépôt des documents à certifier{' '}
                        <span style={{ color: 'var(--stg-muted)', fontWeight: 400 }}>(vue étudiant, après identification)</span>
                    </div>
                    <span className="apercu-modele">{brouillon?.nom || 'Nouveau processus'}</span>
                </div>
                <div className="apercu-cadre"><StageCertifDepotPublic key={apercuEtudiant.n} apercu={apercuEtudiant.etat} /></div>
            </div>
        );
    }

    // ── LA LISTE ────────────────────────────────────────────────────────────

    return (
        <>
            {/* E169 : dans une vignette, « Reprendre l'année précédente » et « Nouveau processus » sont sur l'accueil. */}
            {!seulement && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: 'var(--stg-muted)' }}>
                    <i className="fa-solid fa-circle-info" /> Un processus = une formation, une période, ses livrables. Plusieurs processus par formation et par année.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-outline btn-sm"
                            disabled={!modifiable || enCours || !donnees.annee_precedente}
                            title={donnees.annee_precedente
                                ? `Copier les processus de ${donnees.annee_precedente.libelle}, sans leurs dates`
                                : "Aucune année précédente dans l'application"}
                            onClick={() => setConfirmeN1(true)}>
                        <i className="fa-solid fa-rotate-left" /> Reprendre l'année précédente
                    </button>
                    <button type="button" className="btn btn-green btn-sm" disabled={!modifiable || enCours || !formations.length}
                            title={formations.length ? 'Créer un processus de certification' : "Aucune formation active dans le département"}
                            onClick={() => ouvrir('new')}>
                        <i className="fa-solid fa-plus" /> Nouveau processus
                    </button>
                </div>
            </div>
            )}

            {erreur && !brouillon && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{erreur}</span>
                </div>
            )}

            <div className="card" style={{ marginBottom: 16 }}>
                <table>
                    <thead>
                        <tr>
                            <th>Formation</th><th>Processus</th><th>Période d'activité</th><th>Certification avant le</th>
                            <th>Livrables</th><th>Dossiers</th><th>État</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {processus.length === 0 && (
                            <tr>
                                <td colSpan={8} style={{ textAlign: 'center', color: 'var(--stg-muted)', padding: '24px 14px' }}>
                                    Aucun processus de certification pour {donnees.annee?.libelle || 'cette année'}.
                                    {' '}Créez-en un{donnees.annee_precedente ? ", ou reprenez ceux de l'année précédente" : ''}.
                                </td>
                            </tr>
                        )}
                        {affiches.map((p) => (
                            <tr key={p.id} style={ouvert === p.id ? { background: '#f0fdf4' } : undefined}>
                                <td>{p.formation_nom}</td>
                                <td><strong>{p.nom}</strong></td>
                                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                    {p.periode_debut && p.periode_fin
                                        ? `${dateFr(p.periode_debut)} → ${dateFr(p.periode_fin)}`
                                        : <span style={{ color: '#b45309' }}><i className="fa-solid fa-triangle-exclamation" /> à saisir</span>}
                                </td>
                                <td style={{ fontSize: 12 }}>
                                    {dateFr(p.date_limite_certification)
                                        || <span style={{ color: '#b45309' }}><i className="fa-solid fa-triangle-exclamation" /> à saisir</span>}
                                </td>
                                <td style={{ fontSize: 12 }}>{p.nb_livrables} ({p.nb_obligatoires} oblig.)</td>
                                {/* Les dossiers naissent au premier envoi (lot C.4). */}
                                <td>{p.nb_dossiers ?? '—'}</td>
                                <td>
                                    {p.lance
                                        ? <span className="badge badge-sent"><i className="fa-solid fa-lock" /> Lancé (figé)</span>
                                        : <span className="badge badge-wait">Non lancé</span>}
                                </td>
                                <td className="actions">
                                    <button type="button" className="action-btn" title="Modifier" onClick={() => ouvrir(p.id)}>
                                        <i className="fa-solid fa-pen" />
                                    </button>
                                    <button type="button" className="action-btn" title="Dupliquer (livrables et attestations repris, dates vidées)"
                                            disabled={!modifiable} onClick={() => ouvrir('new', p)}>
                                        <i className="fa-solid fa-copy" />
                                    </button>
                                    <button type="button" className="action-btn" title="Aperçu de la page du maître"
                                            onClick={() => setApercuMaitre({ n: Date.now(), retour: 'Retour aux processus', titre: p.nom,
                                                page: maitreFictif({ nom: p.nom, livrables: p.livrables || [], periodeDebut: p.periode_debut,
                                                    periodeFin: p.periode_fin, formationNom: p.formation_nom }, reglages) })}>
                                        <i className="fa-solid fa-eye" />
                                    </button>
                                    {!p.lance && (
                                        <button type="button" className="action-btn" title="Supprimer ce processus (jamais lancé)"
                                                disabled={!modifiable} onClick={() => setASupprimer(p)}>
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editeur}

            {aSupprimer && (
                <StageModale titre="Supprimer ce processus ?" icone="fa-trash" ton="danger" enCours={enCours}
                             libelleValider="Supprimer" onValider={supprimer} onFermer={() => setASupprimer(null)}>
                    <div><b>{aSupprimer.formation_nom} · {aSupprimer.nom}</b></div>
                    <ul>
                        <li>Il n'a jamais été lancé : aucun lien n'est parti avec lui.</li>
                        <li>Ses {aSupprimer.nb_livrables} livrable(s) et leurs attestations seront supprimés avec lui.</li>
                    </ul>
                </StageModale>
            )}

            {confirmeN1 && (
                <StageModale titre="Reprendre les processus de l'année précédente ?" icone="fa-rotate-left" enCours={enCours}
                             libelleValider="Reprendre" onValider={reprendreN1} onFermer={() => setConfirmeN1(false)}>
                    <div>Chaque processus de {donnees.annee_precedente?.libelle} sera recopié sur {donnees.annee?.libelle} :</div>
                    <ul>
                        <li>sa formation, son nom, son délai de relance, ses livrables et leurs attestations ;</li>
                        <li><b>ses dates ne sont pas copiées</b> — ce sont précisément celles qui changent ;</li>
                        <li>un processus du même nom déjà présent cette année n'est pas dupliqué ;</li>
                        <li>rien n'est envoyé.</li>
                    </ul>
                </StageModale>
            )}

            {bilan && (
                <StageModale titre={bilan.titre} icone="fa-circle-check" ton="succes"
                             onFermer={() => { const apres = bilan.apres; setBilan(null); if (apres) apres(); }}>
                    {bilan.lignes.map((l, i) => <div key={i} style={{ marginBottom: 4 }}>{l}</div>)}
                </StageModale>
            )}
        </>
    );
}

export default StageCertifProcessus;
